import React, { useState } from "react";
import {
  Bell,
  Moon,
  Sun,
  Trash2,
  X,
  CalendarCheck,
  Video as VideoIcon,
  Users as UsersIcon,
  AlertCircle,
} from "lucide-react";
import Dropdown from "./Dropdown";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface HeaderActionsProps {
  darkMode: boolean;
  setDarkMode: (v: boolean) => void;
}

const initialNotifications = [
  {
    id: 1,
    icon: <CalendarCheck className="text-blue-400" size={18} />,
    text: "Team Standup meeting starts in 15 minutes",
  },
  {
    id: 2,
    icon: <VideoIcon className="text-green-400" size={18} />,
    text: 'Sarah invited you to join "Q4 Planning Session"',
  },
  {
    id: 3,
    icon: <UsersIcon className="text-purple-400" size={18} />,
    text: "3 participants joined your meeting room",
  },
  {
    id: 4,
    icon: <AlertCircle className="text-red-400" size={18} />,
    text: "Your account password expires in 7 days",
  },
];

const HeaderActions: React.FC<HeaderActionsProps> = ({
  darkMode,
  setDarkMode,
}) => {
  const navigate = useNavigate();
  const {user, logout} = useAuth();
  const [notifications, setNotifications] = useState(initialNotifications);
  const [clearedNotifications, setClearedNotifications] = useState<any[]>([]);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [notificationsTab, setNotificationsTab] = useState<
    "current" | "cleared"
  >("current");

  const handleClearNotification = (id: number) => {
    const notif = notifications.find((n) => n.id === id);
    if (!notif) return;
    setNotifications(notifications.filter((n) => n.id !== id));
    setClearedNotifications([notif, ...clearedNotifications]);
  };

  const handleClearAll = () => {
    setClearedNotifications([...notifications, ...clearedNotifications]);
    setNotifications([]);
  };

  const handleRestoreNotification = (id: number) => {
    const notif = clearedNotifications.find((n) => n.id === id);
    if (!notif) return;
    setClearedNotifications(clearedNotifications.filter((n) => n.id !== id));
    setNotifications([notif, ...notifications]);
  };

  return (
    <div className="flex items-center gap-6">
      <button
        className={`p-2 rounded-lg transition-all duration-200 group ${
          darkMode ? "hover:bg-white/10" : "hover:bg-gray-200"
        }`}
        onClick={() => setDarkMode(!darkMode)}
      >
        {darkMode ? (
          <Sun className="text-white/70 group-hover:text-white" size={22} />
        ) : (
          <Moon className="text-gray-600 group-hover:text-gray-800" size={22} />
        )}
      </button>
      <Dropdown
        trigger={
          <button
            className={`relative p-2 rounded-lg transition-all duration-200 group ${
              darkMode ? "hover:bg-white/10" : "hover:bg-gray-200"
            }`}
          >
            <Bell
              className={`${
                darkMode
                  ? "text-white/70 group-hover:text-white"
                  : "text-gray-600 group-hover:text-gray-800"
              }`}
              size={22}
            />
            {notifications.length > 0 && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-red-500 to-pink-500 rounded-full border-2 border-white"></span>
            )}
          </button>
        }
        align="right"
        darkMode={darkMode}
      >
        <div
          className={`sm:w-96 w-56 rounded-xl shadow-2xl border p-4 ${
            darkMode
              ? "bg-[#232336] border-white/10"
              : "bg-white border-gray-200"
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pl-3">
            <div className="flex items-center gap-2">
              <h3
                className={`text-lg font-bold ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Notifications
              </h3>
            </div>
            {notifications.length > 0 && (
              <span className="px-2 py-1 bg-green-600/20 text-green-600 text-xs rounded-full font-medium">
                {notifications.length} new
              </span>
            )}
          </div>
          {/* Notifications List */}
          <div className="max-h-64 overflow-y-auto scrollbar-hide">
            {notifications.length === 0 ? (
              <div className="text-center py-8">
                <div
                  className={`${
                    darkMode ? "bg-white/5" : "bg-gray-100"
                  } w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full`}
                >
                  <Bell
                    size={20}
                    className={`${
                      darkMode ? "text-white/40" : "text-gray-400"
                    }`}
                  />
                </div>
                <p
                  className={`text-sm ${
                    darkMode ? "text-white/60" : "text-gray-600"
                  }`}
                >
                  No notifications
                </p>
                <p
                  className={`text-xs mt-1 ${
                    darkMode ? "text-white/40" : "text-gray-500"
                  }`}
                >
                  You're all caught up!
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.slice(0, 4).map((n) => (
                  <div
                    key={n.id}
                    className={`group p-3 rounded-lg border transition-all duration-200 cursor-pointer ${
                      darkMode
                        ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                        : "bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">{n.icon}</div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm leading-relaxed ${
                            darkMode ? "text-white/90" : "text-gray-900"
                          }`}
                        >
                          {n.text}
                        </p>
                        <p
                          className={`text-xs mt-1 ${
                            darkMode ? "text-white/40" : "text-gray-500"
                          }`}
                        >
                          Just now
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Footer */}
          {notifications.length > 0 && (
            <div
              className={`mt-4 pt-3 border-t ${
                darkMode ? "border-white/10" : "border-gray-200"
              }`}
            >
              <button
                className="w-full text-center text-sm font-medium py-2 px-4 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 hover:scale-105 text-white transition-all duration-200 shadow-lg"
                onClick={() => setShowNotificationsModal(true)}
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      </Dropdown>
      <Dropdown
        trigger={
          <div
            className={`flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2 rounded-xl cursor-pointer transition-all duration-200 ${
              darkMode ? "hover:bg-white/10" : "hover:bg-gray-100"
            }`}
          >
            <img
              src="https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"
              alt="profile"
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white/20 flex-shrink-0"
            />
            <div className="hidden sm:block text-left min-w-0 flex-1">
              <div
                className={`text-xs sm:text-sm font-medium truncate ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {user ? user?.first_name + " " + user?.last_name : "Guest User"}
              </div>
              <div
                className={`text-xs ${
                  darkMode ? "text-white/60" : "text-gray-500"
                }`}
              >
                Online
              </div>
            </div>
            <svg
              className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${
                darkMode ? "text-white/40" : "text-gray-400"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        }
        align="right"
        darkMode={darkMode}
      >
        <div className="w-full max-w-xs sm:max-w-sm">
          {/* Header */}
          <div className="p-4 sm:p-6 pb-3 sm:pb-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="relative flex-shrink-0">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full p-0.5 bg-gradient-to-r from-pink-500 to-red-500">
                  <div className="w-full h-full rounded-full overflow-hidden">
                    <img
                      src="https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"
                      alt="avatar"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3
                  className={`text-base sm:text-lg font-bold truncate ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {user ? user?.first_name + " " + user?.last_name : "Guest User"} 
                </h3>
                <p
                  className={`text-xs sm:text-sm truncate ${
                    darkMode ? "text-white/60" : "text-gray-600"
                  }`}
                >
                  {user ? user.email : "guest@example.com"}
                </p>
                <div
                  className={`text-xs sm:text-sm mt-1 ${
                    darkMode ? "text-white/70" : "text-gray-600"
                  }`}
                >
                  Software Engineer
                </div>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="px-2 sm:px-3 pb-3">
            <div className="space-y-0.5 sm:space-y-1">
              <button
                onClick={() => navigate("/settings")}
                className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-left transition-all duration-200 group ${
                  darkMode ? "hover:bg-white/10" : "hover:bg-gray-50"
                }`}
              >
                <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-600 to-red-500 shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-xs sm:text-sm font-semibold truncate ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Settings
                  </div>
                  <div
                    className={`text-xs truncate ${
                      darkMode ? "text-white/50" : "text-gray-500"
                    }`}
                  >
                    Preferences & privacy
                  </div>
                </div>
                <svg
                  className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${
                    darkMode ? "text-white/30" : "text-gray-400"
                  } group-hover:scale-110 transition-transform duration-200`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
              <button
                onClick={() => navigate("/help")}
                className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-left transition-all duration-200 group ${
                  darkMode ? "hover:bg-white/10" : "hover:bg-gray-50"
                }`}
              >
                <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-600 to-red-500 shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-xs sm:text-sm font-semibold truncate ${
                      darkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Help & Support
                  </div>
                  <div
                    className={`text-xs truncate ${
                      darkMode ? "text-white/50" : "text-gray-500"
                    }`}
                  >
                    Get assistance
                  </div>
                </div>
                <svg
                  className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${
                    darkMode ? "text-white/30" : "text-gray-400"
                  } group-hover:scale-110 transition-transform duration-200`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
            <div
              className={`my-2 sm:my-3 mx-2 sm:mx-3 h-px ${
                darkMode
                  ? "bg-gradient-to-r from-transparent via-white/10 to-transparent"
                  : "bg-gradient-to-r from-transparent via-gray-200 to-transparent"
              }`}
            ></div>
            <button
              onClick={logout}
              className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-left transition-all duration-200 group ${
                darkMode
                  ? "hover:bg-red-500/10 hover:text-red-400"
                  : "hover:bg-red-50 hover:text-red-600"
              }`}
            >
              <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-600 to-red-500 shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs sm:text-sm font-semibold truncate">
                  Sign Out
                </div>
                <div
                  className={`text-xs truncate ${
                    darkMode ? "text-white/50" : "text-gray-500"
                  }`}
                >
                  Log out of your account
                </div>
              </div>
              <svg
                className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${
                  darkMode ? "text-white/30" : "text-gray-400"
                } group-hover:scale-110 transition-transform duration-200`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </Dropdown>

      {/* Notifications Modal */}
      {showNotificationsModal && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm ${
            darkMode ? "bg-black/60" : "bg-gray-900/40"
          }`}
        >
          <div
            className={`${
              darkMode ? "bg-[#232336]" : "bg-white"
            } rounded-2xl shadow-2xl p-8 md:p-12 w-[90vw] max-w-4xl relative max-h-[90vh] overflow-y-auto scrollbar-hide`}
          >
            <button
              className={`absolute top-6 right-6 transition-colors duration-200 ${
                darkMode
                  ? "text-white/60 hover:text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              onClick={() => setShowNotificationsModal(false)}
            >
              <X size={28} />
            </button>
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-1 h-6 bg-gradient-to-b from-pink-600 to-red-500 rounded-full"></div>
                <h2
                  className={`text-3xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Notifications
                </h2>
              </div>
            </div>
            <div
              className={`flex gap-1 mb-8 rounded-xl p-1 ${
                darkMode ? "bg-white/5" : "bg-gray-100"
              }`}
            >
              <button
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  notificationsTab === "current"
                    ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
                    : darkMode
                    ? "text-white/60 hover:text-white hover:bg-white/5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                }`}
                onClick={() => setNotificationsTab("current")}
              >
                Current ({notifications.length})
              </button>
              <button
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  notificationsTab === "cleared"
                    ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
                    : darkMode
                    ? "text-white/60 hover:text-white hover:bg-white/5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                }`}
                onClick={() => setNotificationsTab("cleared")}
              >
                Cleared ({clearedNotifications.length})
              </button>
            </div>
            {notificationsTab === "current" && (
              <div className="space-y-6">
                {notifications.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`${
                        darkMode ? "bg-white/5" : "bg-gray-100"
                      } w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full`}
                    >
                      <Bell
                        size={32}
                        className={`${
                          darkMode ? "text-white/40" : "text-gray-400"
                        }`}
                      />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      No notifications
                    </h3>
                    <p
                      className={`${
                        darkMode ? "text-white/60" : "text-gray-600"
                      }`}
                    >
                      You're all caught up! Check back later for updates.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4">
                      {notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`group rounded-xl p-6 border transition-all duration-200 ${
                            darkMode
                              ? "bg-white/5 border-white/10 hover:border-white/20"
                              : "bg-gray-50 border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 mt-1">{n.icon}</div>
                            <div className="flex-1 min-w-0">
                              <p
                                className={`text-base leading-relaxed mb-2 ${
                                  darkMode ? "text-white/90" : "text-gray-900"
                                }`}
                              >
                                {n.text}
                              </p>
                              <div
                                className={`flex items-center gap-4 text-sm ${
                                  darkMode ? "text-white/40" : "text-gray-500"
                                }`}
                              >
                                <span>Just now</span>
                                <span>•</span>
                                <span>Unread</span>
                              </div>
                            </div>
                            <button
                              className={`flex-shrink-0 p-2 rounded-lg transition-all duration-200 group-hover:opacity-100 opacity-0 ${
                                darkMode
                                  ? "hover:bg-red-600/20 text-white/60 hover:text-red-400"
                                  : "hover:bg-red-50 text-gray-600 hover:text-red-600"
                              }`}
                              onClick={() => handleClearNotification(n.id)}
                              aria-label="Clear notification"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      className={`flex justify-center pt-6 border-t ${
                        darkMode ? "border-white/10" : "border-gray-200"
                      }`}
                    >
                      <button
                        className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:scale-105 text-white font-semibold py-3 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleClearAll}
                        disabled={notifications.length === 0}
                      >
                        Clear All Notifications
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            {notificationsTab === "cleared" && (
              <div className="space-y-6">
                {clearedNotifications.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`${
                        darkMode ? "bg-white/5" : "bg-gray-100"
                      } w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full`}
                    >
                      <Trash2
                        size={32}
                        className={`${
                          darkMode ? "text-white/40" : "text-gray-400"
                        }`}
                      />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      No cleared notifications
                    </h3>
                    <p
                      className={`${
                        darkMode ? "text-white/60" : "text-gray-600"
                      }`}
                    >
                      Your cleared notifications will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {clearedNotifications.map((n) => (
                      <div
                        key={n.id}
                        className={`group rounded-xl p-6 border transition-all duration-200 ${
                          darkMode
                            ? "bg-white/5 border-white/10 hover:border-white/20"
                            : "bg-gray-50 border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 mt-1">{n.icon}</div>
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-base leading-relaxed mb-2 ${
                                darkMode ? "text-white/90" : "text-gray-900"
                              }`}
                            >
                              {n.text}
                            </p>
                            <div
                              className={`flex items-center gap-4 text-sm ${
                                darkMode ? "text-white/40" : "text-gray-500"
                              }`}
                            >
                              <span>Earlier</span>
                            </div>
                          </div>
                          <button
                            className={`flex-shrink-0 p-2 rounded-lg transition-all duration-200 ${
                              darkMode
                                ? "hover:bg-white/10 text-white/60 hover:text-white"
                                : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                            }`}
                            onClick={() => handleRestoreNotification(n.id)}
                            aria-label="Restore notification"
                          >
                            Restore
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderActions;
